/* eslint-disable no-nested-ternary */
import { useContext, useEffect, useState } from 'react';

import '../../../ozoner.scss';

import {
	validationItem,
	validationItemState,
} from 'components/hocs/messages/messages';
import { FinancialFormTypeEnum } from 'models/financial-form/enums/financial-form.enums';
import { PopulatedCreditLineFinancialForm } from 'models/financial-form/interfaces/financial-form.interfaces';
import { moicPerNumberOfWeeks } from 'models/inventory/enums/vehicle.enum';
import { LoanOffer } from 'models/loan-offer/controllers/loan-offer.controller';
import { PaymentLinkType } from 'models/openpay/enums/openpay.enums';
import { isPaymentLinkCompleted } from 'models/openpay/interfaces/openpay.interface';
import { WhitelistNameEnum } from 'models/whitelist/interfaces/whitelist.interface';
import { ozonerContext } from '../../OzonerContext';
import { useValidOffer } from './useValidOffer';
import { DevolutionAction, ScheduleAction } from './SolicitudActions';
import {
	MaxFinancingAmountInput,
	MaxPeriodicPaymentInput,
	GenericOpenpayLink,
	LoanIdPayjoyInput,
	OfferCalculator,
	ValidateOfferAction,
} from './SolicitudNumberInputs';
import { SkuSection } from './SkuSection';
import {
	useVehicleDataCompleteness,
	VehicleDataValidation,
} from './VehicleDataValidation';
import { referencesVS } from './constants';

function ApprovedSection() {
	const { financialForm, vehicle, paymentLinks } = useContext(ozonerContext);

	const isCreditLinePayjoy =
		financialForm.form.type === FinancialFormTypeEnum.creditLine;

	const loanForm = financialForm.form as any;
	const isPayJoyEmployeesWhitelist =
		loanForm.whitelistName === WhitelistNameEnum.payJoyEmployees;
	const loanDuration: number | undefined = isCreditLinePayjoy
		? loanForm.loanDuration
		: loanForm.loanApplication?.loanDuration;

	const downPaymentAmount: number | undefined = isCreditLinePayjoy
		? loanForm.downPayment
		: loanForm.loanApplication?.downPayment;

	const [hasLoanTime, sethasLoanTime] = useState(false);
	const [hasDownPayment, setHasDownPayment] = useState(false);
	const { validOffer, validProvider } = useValidOffer();
	const [canSchedule, setCanSchedule] = useState(false);
	const [validPersonalRefs, setValidPersonalRefs] = useState(false);

	const { hasVehicleDataComplete } = useVehicleDataCompleteness(vehicle);

	const offer = loanForm.offer as LoanOffer | undefined;
	const selectedOffer: LoanOffer | undefined =
		offer && offer.duration === loanDuration ? offer : undefined;

	const moic = selectedOffer
		? (selectedOffer.periodicPayment * selectedOffer.duration) /
		  selectedOffer.financingAmount
		: undefined;

	useEffect(() => {
		setHasDownPayment(false);
		sethasLoanTime(!!loanDuration);
		paymentLinks.forEach((link) => {
			if (
				link.paymentLinkType === PaymentLinkType.DownPayment &&
				isPaymentLinkCompleted(link.status)
			) {
				setHasDownPayment(true);
			}
		});
	}, [financialForm.form, vehicle, paymentLinks, loanDuration]);

	useEffect(() => {
		setCanSchedule(
			isCreditLinePayjoy
				? !!vehicle &&
						validPersonalRefs &&
						hasLoanTime &&
						hasVehicleDataComplete &&
						(isPayJoyEmployeesWhitelist ||
							!!(financialForm.form as PopulatedCreditLineFinancialForm)
								.loanIdPayjoy)
				: !!vehicle &&
						hasLoanTime &&
						hasVehicleDataComplete &&
						validOffer &&
						!!moic &&
						!!loanDuration &&
						moic >= moicPerNumberOfWeeks[loanDuration] &&
						hasDownPayment,
		);
	}, [
		validPersonalRefs,
		hasLoanTime,
		hasDownPayment,
		hasVehicleDataComplete,
		validOffer,
		financialForm,
		vehicle,
		moic,
		loanDuration,
		isCreditLinePayjoy,
	]);

	useEffect(() => {
		referencesVS
			.validate(loanForm?.personalReferences ?? {}, {
				abortEarly: false,
			})
			.then(() => {
				setValidPersonalRefs(true);
			})
			.catch(() => {
				setValidPersonalRefs(false);
			});
	}, [loanForm, isCreditLinePayjoy]);

	return (
		<>
			<SkuSection />
			{isCreditLinePayjoy && !isPayJoyEmployeesWhitelist && (
				<LoanIdPayjoyInput />
			)}
			{isCreditLinePayjoy && <MaxPeriodicPaymentInput />}
			{isCreditLinePayjoy && isPayJoyEmployeesWhitelist && (
				<MaxFinancingAmountInput />
			)}
			<OfferCalculator />
			<ValidateOfferAction />
			{validOffer &&
				validProvider &&
				downPaymentAmount !== undefined &&
				downPaymentAmount > 0 && (
					<GenericOpenpayLink type={PaymentLinkType.DownPayment} />
				)}
			<VehicleDataValidation />
			<div
				id="validate-soli-ozoner"
				className="p_md border_b_solid border_1 border_neutral_400 display_flex flex_col flex_gap_md"
			>
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						vehicle ? validationItemState.valid : validationItemState.invalid,
						{
							[validationItemState.valid]: 'Moto asignada',
							[validationItemState.invalid]: 'Moto sin asignar',
						},
					)}
				</div>
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						hasLoanTime
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Plan de pagos seleccionado',
							[validationItemState.invalid]: 'Plan de pagos sin asignar',
						},
					)}
				</div>
				<div className="display_flex flex_gap_xs flex_align_center">
					{validationItem(
						validOffer
							? validationItemState.valid
							: validationItemState.invalid,
						{
							[validationItemState.valid]: 'Oferta válida',
							[validationItemState.invalid]: 'Sin oferta u oferta inválida',
						},
					)}
				</div>
				{isCreditLinePayjoy && !isPayJoyEmployeesWhitelist ? (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(
							(financialForm?.form as PopulatedCreditLineFinancialForm)
								?.loanIdPayjoy
								? validationItemState.valid
								: validationItemState.invalid,
							{
								[validationItemState.valid]: 'ID Crédito PayJoy asignado',
								[validationItemState.invalid]: 'ID Crédito PayJoy sin asignar',
							},
						)}
					</div>
				) : isCreditLinePayjoy && isPayJoyEmployeesWhitelist ? null : (
					<>
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(
								moic !== undefined &&
									loanDuration !== undefined &&
									moic >= moicPerNumberOfWeeks[loanDuration]
									? validationItemState.valid
									: validationItemState.invalid,
								{
									[validationItemState.valid]: 'MOIC valido',
									[validationItemState.invalid]: 'MOIC demasiado bajo',
								},
							)}
						</div>
						<div className="display_flex flex_gap_xs flex_align_center">
							{validationItem(
								hasDownPayment
									? validationItemState.valid
									: validationItemState.invalid,
								{
									[validationItemState.valid]: 'Enganche pagado',
									[validationItemState.invalid]: 'Enganche pendiente',
								},
							)}
						</div>
					</>
				)}
				{isCreditLinePayjoy && (
					<div className="display_flex flex_gap_xs flex_align_center">
						{validationItem(
							validPersonalRefs
								? validationItemState.valid
								: validationItemState.invalid,
							{
								[validationItemState.valid]: 'Referencias personales completas',
								[validationItemState.invalid]:
									'Referencias personales incompletas',
							},
						)}
					</div>
				)}
				<ScheduleAction disable={!canSchedule} />
				{!isCreditLinePayjoy && <DevolutionAction />}
			</div>
		</>
	);
}
export default ApprovedSection;
